/**
 * Satış potansiyeli değerlendirmesi ve takip önceliklendirmesi (v4.6). Sözleşme: docs/spec/castinflow/satis-potansiyeli.md;
 * migration 20261020_0288 (`sales_assessments`).
 *
 * - Değerlendirme kullanıcının bir temas (tamamlanan aktivite) anındaki TAHMİNİDİR; ölçülmüş başarı olasılığı değildir ve fırsatın
 *   `probability` alanını değiştirmez. Yüzde için varsayılan değer YOKTUR.
 * - "Değerlendirilemedi" sıfır sayılmaz: güncel durum `not_assessable` olur, yüzde boş kalır, eşik süzgecine girmez.
 * - %100 kazanıldı, %0 kaybedildi DEĞİLDİR: değerlendirme hiçbir durum işlemi üretmez.
 */
import { z } from 'zod';
import { ACTIVITY_KINDS } from './activities.js';
import { lookupKeySchema } from './sales.js';
const uuid = z.guid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'tarih YYYY-AA-GG olmalı');
const optText = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((s) => (s === '' ? null : s))
    .nullable();
// ---------------------------------------------------------------------------------------------------------------------
// Sabit kümeler
// ---------------------------------------------------------------------------------------------------------------------
export const SALES_ASSESSMENT_MODES = ['off', 'optional', 'required'];
export const SALES_ASSESSMENT_MODE_LABELS = {
    off: 'Kapalı',
    optional: 'İsteğe bağlı',
    required: 'Zorunlu',
};
/** Değerlendirilebilen satış özneleri */
export const SALES_ASSESSMENT_SUBJECT_TYPES = ['lead', 'opportunity'];
export const SALES_ASSESSMENT_OUTCOMES = ['assessed', 'not_assessable'];
/** Kaynak: ilk sürümde yalnız `manual` yazılır; `stage_default` ve `model` ileride ayrı kaynak olarak eklenir */
export const SALES_ASSESSMENT_SOURCES = ['manual', 'stage_default', 'model'];
/** Liste ve özetlerde güncel durum: hiç değerlendirme yoksa `none` ("Bilinmiyor") */
export const SALES_POTENTIAL_STATUSES = ['assessed', 'not_assessable', 'none'];
export const SALES_POTENTIAL_STATUS_LABELS = {
    assessed: 'Değerlendirildi',
    not_assessable: 'Değerlendirilemedi',
    none: 'Bilinmiyor',
};
/** Kısa rehber işaretleri (her biri evet / hayır / boş) */
export const SALES_ASSESSMENT_CRITERIA = [
    'need',
    'budget',
    'decisionMaker',
    'timing',
    'nextStep',
];
export const SALES_ASSESSMENT_CRITERIA_LABELS = {
    need: 'İhtiyaç',
    budget: 'Bütçe uyumu',
    decisionMaker: 'Karar verici',
    timing: 'Satın alma zamanı',
    nextStep: 'Kararlaştırılmış sonraki adım',
};
/** Arayüzün değerlendirme alanında göstereceği açıklama (yüzde ölçülmüş olasılık değildir) */
export const SALES_ASSESSMENT_NOTICE = 'Bu yüzde sizin tahmininizdir; ölçülmüş başarı olasılığı değildir. Durumu (kazanıldı/kaybedildi) değiştirmez.';
export const SALES_ASSESSMENT_DEFAULT_KINDS = ['call', 'meeting', 'visit'];
/**
 * "Eski değerlendirme" eşiği (gün): güncel değerlendirme bundan eskiyse arayüzde "Eski değerlendirme" işaretlenir ve Bugün'ün ağırlıklı
 * tahmininde ayrı sayılır (tema uyarlaması C/D, 21 Ekim 2026). Tek kaynak: web gösterimi ve Bugün özeti bu sabiti kullanır.
 */
export const SALES_ASSESSMENT_STALE_DAYS = 30;
// ---------------------------------------------------------------------------------------------------------------------
// Kiracı politikası (`module_settings`, modül `sales_assessment`)
// ---------------------------------------------------------------------------------------------------------------------
export const salesAssessmentPolicySchema = z
    .object({
    mode: z.enum(SALES_ASSESSMENT_MODES).default('off'),
    /** Değerlendirmenin istendiği aktivite türleri */
    activityKinds: z
        .array(z.enum(ACTIVITY_KINDS))
        .min(1, 'en az bir aktivite türü seçin')
        .max(ACTIVITY_KINDS.length)
        .refine((xs) => new Set(xs).size === xs.length, 'aynı tür iki kez seçilemez')
        .default([...SALES_ASSESSMENT_DEFAULT_KINDS]),
    /** Açık adaylar (dönüştürülmemiş; niteliksiz/tekrar eden hariç) kapsamda mı */
    includeLeads: z.boolean().default(true),
    /** Kapsamdaki fırsat aşamaları; `null` = bütün aşamalardaki AÇIK fırsatlar */
    opportunityStages: z
        .array(lookupKeySchema)
        .min(1, 'en az bir aşama seçin ya da bütün aşamalar seçeneğini kullanın')
        .max(50)
        .refine((xs) => new Set(xs).size === xs.length, 'aynı aşama iki kez seçilemez')
        .nullable()
        .default(null),
    /** Yüzde yerine "değerlendirilemedi" + gerekçe girilebilir mi */
    allowNotAssessable: z.boolean().default(true),
    /** "Öncelikli takip" listesinin eşiği (potansiyel ≥ eşik) */
    priorityThreshold: z.number().int().min(0).max(100).default(50),
})
    .strict();
// ---------------------------------------------------------------------------------------------------------------------
// G3 (sahip kararı A, 24 Ekim 2026): "öncelikli" TEK tanımı — aday listesi, fırsat listesi, satış takibi, Genel Bakış "Öncelikli",
// "Bugün" takip kartı ve pazarlama "Öncelikli" aynı kuralı kullanır (test kilidi: `sales-priority.test.ts`, API
// `sales-priority-lock.test.ts`).
// ---------------------------------------------------------------------------------------------------------------------
/**
 * Öncelik kuralı: açık kayıt + GÜNCEL değerlendirme yüzde (`assessed`) + yüzde ≥ çalışma alanı eşiği (`priorityThreshold`).
 *  - Karşılaştırma her yerde `≥` (`gte`); eşik politikadan gelir, ekranda sabit "%50" yazılmaz.
 *  - "Değerlendirilemedi" ve hiç değerlendirilmemiş kayıt öncelikli DEĞİLDİR (boş ≠ 0, boş ≠ eşik üstü).
 *  - Eskilik: {@link SALES_ASSESSMENT_STALE_DAYS} günden eski değerlendirme listeden DÜŞMEZ, "Eski değerlendirme" işaretiyle kalır
 *    ve "Yeniden değerlendirme" listesine de girer (eskilik süre esaslıdır: temas anından bu yana 30 × 24 saat — {@link isSalesAssessmentStale}).
 *  - Politika kapalıysa ya da adaylar kapsam dışıysa (aday için) öncelik listesi YOKTUR (`null`): sayı gösterilmez, "—" + neden.
 * Manuel satış potansiyeli önceliklendirme ölçüsüdür; ağırlıklı satış tahmininde KULLANILMAZ (tahmin = tutar × fırsat olasılığı).
 */
export const SALES_PRIORITY_OPERATOR = 'gte';
export const SALES_PRIORITY_DEFINITION = 'Öncelikli: açık kayıtlardan güncel satış potansiyeli yüzde olarak girilmiş ve çalışma alanının öncelik eşiğine eşit ya da üzerinde olanlar. Değerlendirilemedi ya da hiç değerlendirilmemiş kayıt öncelikli sayılmaz; 30 günden eski değerlendirme listede "Eski değerlendirme" işaretiyle kalır.';
/** Öncelik eşiği (kayıt türüne göre); `null` = bu türde öncelik listesi yok (politika kapalı / adaylar kapsam dışı) */
export function salesPriorityThreshold(policy, recordType) {
    if (policy.mode === 'off')
        return null;
    if (recordType === 'lead' && !policy.includeLeads)
        return null;
    return policy.priorityThreshold;
}
/** Liste süzgeci yüklemi (`potential` alanı yalnız güncel değerlendirme yüzdeyse dolu — liste derleyicisi) */
export function salesPriorityPredicate(threshold) {
    return {
        op: 'predicate',
        field: 'potential',
        operator: SALES_PRIORITY_OPERATOR,
        value: threshold,
    };
}
/** Kayıtlı/hazır süzgeç satırı (liste ekranlarının "Potansiyel ≥ eşik" ön ayarı) */
export function salesPriorityFilterRows(threshold) {
    return [{ field: 'potential', operator: SALES_PRIORITY_OPERATOR, value: threshold }];
}
/** Saf: güncel potansiyel öncelikli mi (kart/rozet; sunucu SQL'i ile aynı kural) */
export function isSalesPriority(current, threshold) {
    return (threshold !== null &&
        threshold !== undefined &&
        current.status === 'assessed' &&
        current.percent !== null &&
        current.percent >= threshold);
}
/** Eskilik sınırı (UTC an): temas anı bundan ÖNCEYSE değerlendirme eskidir */
export function salesAssessmentStaleBefore(now) {
    return new Date(now.getTime() - SALES_ASSESSMENT_STALE_DAYS * 86_400_000);
}
/** Saf: değerlendirme eski mi (süre esaslı; sunucu `occurred_at < şimdi − 30 gün` ile aynı) */
export function isSalesAssessmentStale(occurredAt, now) {
    const at = typeof occurredAt === 'string' ? new Date(occurredAt) : occurredAt;
    if (Number.isNaN(at.getTime()))
        return false;
    return at.getTime() < salesAssessmentStaleBefore(now).getTime();
}
/** Satır yoksa geçerli politika: KAPALI (mevcut kiracılarda kendiliğinden zorunlu olmaz) */
export const SALES_ASSESSMENT_POLICY_DEFAULTS = salesAssessmentPolicySchema.parse({});
export const salesAssessmentPolicyViewSchema = z.object({
    policy: salesAssessmentPolicySchema,
    /** Çalışma alanı henüz kaydetmediyse ürün varsayılanı (kapalı) */
    isDefault: z.boolean(),
    /** İyimser kilit sürümü (kayıt yoksa 0) */
    version: z.number().int(),
    updatedAt: z.string().nullable(),
    updatedBy: z.object({ id: z.string(), name: z.string() }).nullable(),
});
export const updateSalesAssessmentPolicyInputSchema = z
    .object({
    policy: salesAssessmentPolicySchema,
    /** İlk kayıtta 0; sonra görünümdeki `version` */
    expectedVersion: z.number().int().min(0),
})
    .strict();
// ---------------------------------------------------------------------------------------------------------------------
// Değerlendirme girdisi
// ---------------------------------------------------------------------------------------------------------------------
export const salesAssessmentCriteriaSchema = z
    .object({
    need: z.boolean().nullable().optional(),
    budget: z.boolean().nullable().optional(),
    decisionMaker: z.boolean().nullable().optional(),
    timing: z.boolean().nullable().optional(),
    nextStep: z.boolean().nullable().optional(),
})
    .strict();
/**
 * Değerlendirme: ya `percent` (0–100 tamsayı) ya da `notAssessable.reason` (3–500) — ikisi birden ya da hiçbiri olamaz.
 * Özne açıkça seçilir ve aktivitenin bağlı olduğu açık bir aday/fırsat olmalıdır (sunucu denetler).
 */
export const salesAssessmentInputSchema = z
    .object({
    subjectType: z.enum(SALES_ASSESSMENT_SUBJECT_TYPES),
    subjectId: uuid,
    percent: z.number().int('tamsayı olmalı').min(0).max(100).optional(),
    notAssessable: z
        .object({
        reason: z
            .string()
            .trim()
            .min(3, 'gerekçe en az 3 karakter')
            .max(500, 'gerekçe en çok 500 karakter'),
    })
        .strict()
        .optional(),
    criteria: salesAssessmentCriteriaSchema.optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.percent === undefined && v.notAssessable === undefined)
        ctx.addIssue({
            code: 'custom',
            path: ['percent'],
            message: 'yüzde girin ya da "değerlendirilemedi" seçin',
        });
    if (v.percent !== undefined && v.notAssessable !== undefined)
        ctx.addIssue({
            code: 'custom',
            path: ['notAssessable'],
            message: 'yüzde ile "değerlendirilemedi" birlikte girilemez',
        });
});
/**
 * `POST /records/activity/:id/progress` gövdesi (v4.6): ilerleme + isteğe bağlı değerlendirme. Değerlendirme yalnız tamamlamada
 * (ilerleme 100) kabul edilir; zaten tamamlanmış aktivitede istek değişiklik üretmez.
 */
export const activityCompletionInputSchema = z
    .object({
    progress: z.number().int().min(0).max(100),
    assessment: salesAssessmentInputSchema.optional(),
})
    .strict();
/** Reddin gerekçeleri (`422 VALIDATION_FAILED`, `details.reason`) */
export const SALES_ASSESSMENT_REFUSAL_REASONS = [
    /** Kapsamdaki tamamlama zorunlu değerlendirme olmadan */
    'assessment_required',
    /** Politika kapalıyken değerlendirme gönderildi */
    'assessment_disabled',
    /** Aktivite türü ya da bağlı kayıtlar kapsam dışında */
    'assessment_out_of_scope',
    /** Özne aktivitenin bağlı olduğu açık aday/fırsat değil */
    'assessment_subject_invalid',
    /** Politika "değerlendirilemedi" seçeneğine izin vermiyor */
    'not_assessable_disallowed',
    /** Değerlendirme yalnız tamamlamada girilir */
    'assessment_not_completing',
];
export const SALES_ASSESSMENT_REFUSAL_TEXT = {
    assessment_required: 'Bu aktiviteyi tamamlamak için satış potansiyeli değerlendirmesi gerekli: yüzde girin ya da izinliyse "Değerlendirilemedi" seçip gerekçe yazın.',
    assessment_disabled: 'Satış potansiyeli değerlendirmesi bu çalışma alanında kapalı.',
    assessment_out_of_scope: 'Bu aktivite için satış potansiyeli değerlendirmesi istenmiyor (aktivite türü ya da bağlı kayıtlar kapsam dışında).',
    assessment_subject_invalid: 'Değerlendirme yalnız aktivitenin bağlı olduğu açık aday ya da fırsat için girilebilir.',
    not_assessable_disallowed: '"Değerlendirilemedi" seçeneği bu çalışma alanında kapalı; bir yüzde girin.',
    assessment_not_completing: 'Değerlendirme yalnız aktivite tamamlanırken girilir.',
};
// ---------------------------------------------------------------------------------------------------------------------
// Görünümler
// ---------------------------------------------------------------------------------------------------------------------
/** Özne başına güncel potansiyel özeti (güncel = `occurred_at` en yeni; eşitse kayıt anı en yeni) */
export const salesPotentialSummarySchema = z.object({
    status: z.enum(SALES_POTENTIAL_STATUSES),
    /** Yalnız `assessed` iken dolu */
    percent: z.number().int().nullable(),
    /** Dayandığı temasın anı */
    occurredAt: z.string().nullable(),
    assessmentId: z.string().nullable(),
});
export const salesAssessmentViewSchema = z.object({
    id: z.string(),
    subjectType: z.enum(SALES_ASSESSMENT_SUBJECT_TYPES),
    subjectId: z.string(),
    /** Fırsat geçmişinde: değerlendirme fırsatın kaynak adayına verilmişti ("adayken") */
    fromLead: z.boolean(),
    outcome: z.enum(SALES_ASSESSMENT_OUTCOMES),
    percent: z.number().int().nullable(),
    reason: z.string().nullable(),
    criteria: z
        .object({
        need: z.boolean().nullable(),
        budget: z.boolean().nullable(),
        decisionMaker: z.boolean().nullable(),
        timing: z.boolean().nullable(),
        nextStep: z.boolean().nullable(),
    })
        .nullable(),
    source: z.enum(SALES_ASSESSMENT_SOURCES),
    occurredAt: z.string(),
    assessedAt: z.string(),
    assessedBy: z.object({ id: z.string(), name: z.string() }).nullable(),
    /** Dayandığı aktivite (çağırana görünürse) */
    activity: z.object({ id: z.string(), name: z.string(), kind: z.enum(ACTIVITY_KINDS) }).nullable(),
    /** Öznenin güncel değerlendirmesi mi */
    current: z.boolean(),
});
export const salesAssessmentHistoryQuerySchema = z
    .object({
    subjectType: z.enum(SALES_ASSESSMENT_SUBJECT_TYPES),
    subjectId: uuid,
})
    .strict();
export const salesAssessmentHistoryResponseSchema = z.object({
    subject: z.object({
        type: z.enum(SALES_ASSESSMENT_SUBJECT_TYPES),
        id: z.string(),
        title: z.string(),
    }),
    current: salesPotentialSummarySchema,
    /** En yeni önce (`occurredAt`, sonra kayıt anı) */
    items: z.array(salesAssessmentViewSchema),
    /** Sunucu tavanı (200) aşıldı */
    truncated: z.boolean(),
});
/**
 * Tamamlama formunun kapsam sorusu: `GET /sales/assessment-scope?activityId=` (var olan aktivite) ya da
 * `?kind=&recordIds=a,b` (henüz oluşturulmamış "aktivite kaydet" formu: asıl ilgili + bağlanacak fırsatlar).
 */
export const salesAssessmentScopeQuerySchema = z
    .object({
    activityId: uuid.optional(),
    kind: z.enum(ACTIVITY_KINDS).optional(),
    recordIds: z
        .string()
        .max(37 * 21)
        .transform((s) => (s === '' ? [] : s.split(',').map((x) => x.trim())))
        .pipe(z.array(uuid).max(21))
        .optional(),
})
    .strict()
    .superRefine((q, ctx) => {
    const byActivity = q.activityId !== undefined;
    const byForm = q.kind !== undefined || q.recordIds !== undefined;
    if (byActivity === byForm)
        ctx.addIssue({
            code: 'custom',
            path: ['activityId'],
            message: 'ya activityId ya da kind + recordIds verin',
        });
    if (!byActivity && q.kind === undefined)
        ctx.addIssue({ code: 'custom', path: ['kind'], message: 'kind gerekli' });
});
/** Kapsam sorusunun "alan yok" nedenleri (arayüz kısa açıklama gösterir; renk/sayı değil metin) */
export const SALES_ASSESSMENT_OUT_OF_SCOPE_REASONS = [
    'disabled',
    'kind',
    'no_subject',
    'not_writable',
];
export const salesAssessmentScopeSchema = z.object({
    mode: z.enum(SALES_ASSESSMENT_MODES),
    /** Değerlendirme alanı gösterilmeli mi (politika açık + tür kapsamda + en az bir değerlendirilebilir özne) */
    inScope: z.boolean(),
    /** Kapsamda ve zorunlu: değerlendirme olmadan tamamlama reddedilir */
    required: z.boolean(),
    allowNotAssessable: z.boolean(),
    /**
     * Alan gösterilmiyorsa nedeni (v4.6 A2; isteğe bağlı — eski sunucu göndermez): `disabled` politika kapalı · `kind` aktivite türü
     * kapsam dışı · `no_subject` bağlı açık (değerlendirilebilir) aday/fırsat yok · `not_writable` değerlendirilebilir kayıt var
     * ama çağıran yazamıyor. Kapsamdaysa `null`.
     */
    outOfScopeReason: z.enum(SALES_ASSESSMENT_OUT_OF_SCOPE_REASONS).nullable().optional(),
    /** Çağıranın yazabildiği, değerlendirilebilir özneler (birden çoksa kullanıcı seçer) */
    subjects: z.array(z.object({
        type: z.enum(SALES_ASSESSMENT_SUBJECT_TYPES),
        id: z.string(),
        title: z.string(),
        current: salesPotentialSummarySchema,
    })),
});
// ---------------------------------------------------------------------------------------------------------------------
// Birleşik "aktivite kaydet" (temas kaydı): tamamlanmış aktivite + değerlendirme + sonraki takip görevi TEK işlemde
// ---------------------------------------------------------------------------------------------------------------------
/** Temas kaydının açıldığı kayıt türleri (asıl ilgili) */
export const SALES_CONTACT_LOG_RECORD_TYPES = [
    'lead',
    'opportunity',
    'company',
    'contact',
];
/** Temas türleri (iş/görev temas değildir; sonraki takip görevi ayrı girilir) */
export const SALES_CONTACT_LOG_KINDS = ['call', 'meeting', 'visit', 'event', 'other'];
export const salesFollowUpInputSchema = z
    .object({
    name: z.string().trim().min(1, 'görev adı gerekli').max(200),
    /** Son tarih (çalışma alanının yerel takvim günü; bugünden önce olamaz) */
    dueOn: isoDate,
    assigneeUserId: uuid,
})
    .strict();
export const salesContactLogInputSchema = z
    .object({
    /** İstek anahtarı (form açılışında bir kez üretilir): aynı belirteç + aynı gövde aynı sonucu döndürür; farklı gövde 409 */
    clientToken: uuid,
    recordType: z.enum(SALES_CONTACT_LOG_RECORD_TYPES),
    recordId: uuid,
    kind: z.enum(SALES_CONTACT_LOG_KINDS),
    name: z.string().trim().min(1, 'konu gerekli').max(200),
    description: optText(10000).optional(),
    /** Temasın gerçekleştiği an (ofsetli ISO 8601); verilmezse sunucu anı. Gelecekte olamaz. */
    occurredAt: z.iso.datetime({ offset: true }).optional(),
    /** Temasla ilgili diğer fırsatlar (aktiviteye ilgili kayıt olarak bağlanır; değerlendirme öznesi olabilir) */
    relatedOpportunityIds: z.array(uuid).max(20).default([]),
    assessment: salesAssessmentInputSchema.optional(),
    followUp: salesFollowUpInputSchema.optional(),
    /** Temas aktivitesinin özel alan değerleri (aktivite oluşturma formuyla aynı kural; zorunlu özel alanlar dahil) */
    customValues: z.record(z.string(), z.unknown()).prefault({}),
})
    .strict();
export const salesContactLogResponseSchema = z.object({
    /** Oluşturulan tamamlanmış aktivite */
    activityId: z.string(),
    assessmentId: z.string().nullable(),
    /** Sonraki takip görevi (aktivite, tür `task`) */
    followUpId: z.string().nullable(),
    /** Aynı belirteçle önceki istek bulundu; yeni kayıt üretilmedi */
    replayed: z.boolean(),
});
/** Anahtarları sıralı JSON değeri (özel alan değerleri: alan sırası özeti değiştirmez; dizi sırası korunur) */
function sortedJson(v) {
    if (Array.isArray(v))
        return v.map(sortedJson);
    if (v !== null && typeof v === 'object') {
        const o = v;
        const out = {};
        for (const k of Object.keys(o).sort())
            out[k] = sortedJson(o[k]);
        return out;
    }
    return v;
}
/**
 * Temas kaydı isteğinin İÇERİK özeti kaynağı (sunucu SHA-256'sını aktivitenin `create_request_hash` alanında saklar). Aynı niyetin
 * yeniden gönderimi aynı metni üretir; özeti değiştirmeyenler: `clientToken`, alan sırası, kimliklerin harf büyüklüğü, ilgili
 * fırsat listesinin sırası/tekrarı, `occurredAt`ın ofset yazımı (aynı an), boş bırakılan isteğe bağlı alanlar.
 */
export function salesContactLogFingerprint(input) {
    const ids = (list) => [...new Set(list.map((x) => x.toLowerCase()))].sort();
    const a = input.assessment;
    const c = a?.criteria;
    const crit = (v) => (v === undefined ? null : v);
    return JSON.stringify([
        'sales.contact-log/1',
        input.recordType,
        input.recordId.toLowerCase(),
        input.kind,
        input.name,
        input.description ?? null,
        input.occurredAt === undefined ? null : new Date(input.occurredAt).toISOString(),
        ids(input.relatedOpportunityIds),
        a === undefined
            ? null
            : [
                a.subjectType,
                a.subjectId.toLowerCase(),
                a.percent ?? null,
                a.notAssessable?.reason ?? null,
                c === undefined ? null : SALES_ASSESSMENT_CRITERIA.map((k) => crit(c[k])),
            ],
        input.followUp === undefined
            ? null
            : [input.followUp.name, input.followUp.dueOn, input.followUp.assigneeUserId.toLowerCase()],
        sortedJson(input.customValues),
    ]);
}
// ---------------------------------------------------------------------------------------------------------------------
// Satış takibi listeleri
// ---------------------------------------------------------------------------------------------------------------------
/**
 * C1 (TI-016, 24 Ekim 2026): dördüncü görünüm "Yeniden değerlendirme" (`stale`) — güncel değerlendirmesi
 * {@link SALES_ASSESSMENT_STALE_DAYS} günden eski açık aday/fırsat ("Bugün" ekranının eskilik kuralıyla aynı: temas anı
 * `şimdi − 30 gün`ten önce). Arka planda puan uydurulmaz; kayıt yeniden görüşülüp değerlendirilince listeden çıkar.
 */
export const SALES_WORKLISTS = ['priority', 'unassessed', 'overdue', 'stale'];
export const SALES_WORKLIST_LABELS = {
    priority: 'Öncelikli takip',
    unassessed: 'Değerlendirme bekleyen yeni adaylar',
    overdue: 'Geciken takipler',
    stale: 'Yeniden değerlendirme',
};
/** Her listenin sıralaması (gizli puan yok; satırlar bu alanları taşır) */
export const SALES_WORKLIST_ORDER = {
    priority: [
        { field: 'overdue', direction: 'desc' },
        { field: 'nextFollowUpOn', direction: 'asc' },
        { field: 'potential', direction: 'desc' },
    ],
    unassessed: [{ field: 'createdAt', direction: 'asc' }],
    overdue: [
        { field: 'oldestOverdueOn', direction: 'asc' },
        { field: 'overdueTaskCount', direction: 'desc' },
    ],
    stale: [{ field: 'potentialAssessedAt', direction: 'asc' }],
};
export const SALES_WORKLIST_ORDER_TEXT = {
    priority: 'Potansiyeli eşiğin üzerindeki açık kayıtlar: önce gecikmiş takibi olanlar, sonra en yakın takip tarihi, sonra yüksek potansiyel.',
    unassessed: 'Hiç değerlendirilmemiş açık adaylar: en eski kayıt önce.',
    overdue: 'Görüntüleyebildiğiniz gecikmiş açık işi/aktivitesi olan bütün açık kayıtlar (potansiyelden bağımsız): en eski son tarih önce.',
    stale: `Son değerlendirmesi ${String(SALES_ASSESSMENT_STALE_DAYS)} günden eski açık kayıtlar: en eski değerlendirme önce. Yeni görüşmede yeniden değerlendirilince listeden çıkar.`,
};
export const salesWorklistQuerySchema = z
    .object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    /** Yalnız sorumlusu (sahibi) ben olanlar */
    mine: z.enum(['0', '1']).optional(),
})
    .strict();
/**
 * Satış takibi satırı. Takip alanları (`lastContactAt`, `nextFollowUpOn`, `openTaskCount`, `overdueTaskCount`, `overdue`,
 * `oldestOverdueOn`) ve listeye girme / sıra YALNIZ çağıranın görebildiği aktivitelerden hesaplanır (aktivitenin kendi ACL'si).
 */
export const salesWorklistItemSchema = z.object({
    record: z.object({
        type: z.enum(SALES_ASSESSMENT_SUBJECT_TYPES),
        id: z.string(),
        title: z.string(),
    }),
    ownerId: z.string().nullable(),
    ownerName: z.string().nullable(),
    potential: z.number().int().nullable(),
    potentialStatus: z.enum(SALES_POTENTIAL_STATUSES),
    /** Güncel değerlendirmenin dayandığı temasın anı */
    potentialAssessedAt: z.string().nullable(),
    /** Son tamamlanan temas (iş/görev türü hariç tamamlanmış aktivite) */
    lastContactAt: z.string().nullable(),
    /** Açık ilk iş/aktivite tarihi (gecikmiş olabilir) */
    nextFollowUpOn: z.string().nullable(),
    openTaskCount: z.number().int(),
    overdueTaskCount: z.number().int(),
    /** Gecikmiş açık işi var (`overdueTaskCount > 0`; öncelik sıralamasının ilk anahtarı) */
    overdue: z.boolean(),
    /** Gecikmiş açık işlerin en eski tarihi */
    oldestOverdueOn: z.string().nullable(),
    createdAt: z.string(),
});
export const salesWorklistResponseSchema = z.object({
    list: z.enum(SALES_WORKLISTS),
    /** Öncelik eşiği (politikadan) */
    threshold: z.number().int(),
    /** Gecikme hesabının günü (çalışma alanı saat diliminde) */
    today: z.string(),
    timeZone: z.string(),
    order: z.array(z.object({ field: z.string(), direction: z.enum(['asc', 'desc']) })),
    orderText: z.string(),
    items: z.array(salesWorklistItemSchema),
    /** İstenen sayıdan fazla kayıt var */
    truncated: z.boolean(),
});
//# sourceMappingURL=sales-assessment.js.map